<template>
  <layout-admin-dashboard-view
    :show-toolbar="false"
    :back-route="{ name: 'admin' }">
    <template #title>
      <span>{{ translateTitleCase("ecommerce.orders.displayTitle") }}</span>
    </template>

    <template #default>
      <custom-crud-list
        ref="crudList"
        v-bind="listConfiguration"
        @crud-list:items-updated="playground">
        <template #cell-createdAt="{ item }">
          <span>{{ translateDate(item.createdAt, "dateWithTime") }}</span>
        </template>
        <template #cell-status="{ item }">
          <span>{{
            tu(`ecommerce.order.fields.statuses.${item.status}`)
          }}</span>
        </template>
        <template #cell-totalWithTax="{ item }">
          <span>{{
            translateNumber(item.Quote.grandTotal, "currency", {
              currency: item.Quote.currency,
            })
          }}</span>
        </template>
        <template #cell-numProducts="{ item }">
          <span>{{ translateNumber(item.Quote.totalProductCount) }}</span>
        </template>
        <template #cell-vendors="{ item }">
          <span v-if="item.Vendors && item.Vendors.length > 0">
            {{ item.Vendors.map((vendor) => vendor.name).join(", ") }}
          </span>
          <span v-else>-</span>
        </template>
        <template #cell-BuyerId="{ item }">
          <span v-if="item?.Buyer?.email">{{ item.Buyer.email }}</span>
          <span v-else>-</span>
        </template>
        <template #cell-estimatedDeliveryDateTime="{ item }">
          <span v-if="item.estimatedDeliveryDateTime">{{
            translateDate(item.estimatedDeliveryDateTime, "dateWithTime")
          }}</span>
          <span v-else>-</span>
        </template>
        <template #cell-payments="{ item }">
          <span v-if="Array.isArray(item.Payments) && item.Payments.length > 0">
            {{
              translate(
                "ecommerce.order.fields.orderPaymentSummaryText",
                getPaymentSummaryForOrder(item)
              )
            }}
          </span>
          <span v-else>
            {{ translate("ecommerce.order.paymentStatus.noPayments") }}
          </span>
        </template>
      </custom-crud-list>

      <modal
        :show="isShowingOrderModal"
        @modal:closed="closeOrderModal"
        ref="orderViewModal"
        size="l"
        title="ecommerce.order.admin.orderSummaryModalTitle">
        <admin-order-summary
          @adminOrderSummary:updated="orderWasUpdated = true"
          v-model="order"></admin-order-summary>
      </modal>
    </template>
  </layout-admin-dashboard-view>
  <modal
    :show="isShowingOrderPayments"
    @modal:closed="isShowingOrderPayments = false"
    v-model="isShowingOrderPayments">
    <div v-for="(payment, index) of orderPayments">
      <h3>
        {{
          tt("ecommerce.order.admin.actions.allPaymentsTitle", {
            count: orderPayments.length,
          })
        }}
      </h3>
      <h5>
        <saffron-link
          :to="{
            name: 'admin-entity-entityType-edit-entityId',
            params: { entityType: 'payment', entityId: payment.id },
          }">
          {{ tu("ecommerce.order.admin.actions.paymentTitle") }}
          {{ payment.id }}
        </saffron-link>
      </h5>

      <base-form
        :config="{ ...paymentForm, buttons: {} }"
        disabled="true"
        v-model="orderPayments[index]"></base-form>
    </div>
  </modal>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import CustomCrudList from "@/client/components/crud/customV1/List.vue";
import Cart from "@/client/components/ecommerce/Cart.vue";
import AdminOrderSummary from "@/client/components/ecommerce/order/AdminOrderSummary.vue";

export default {
  components: { CustomCrudList, Cart, AdminOrderSummary },
  layout: "adminDashboard",
  middleware: {
    acl: "adminUi|read",
  },
  props: {},
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props);
    return { asyncOps, asyncOpsReady, asyncStatus };
  },
  meta: "admin.orders.pageTitle",
  data: function () {
    return {
      order: false,
      userForm: false,
      listConfiguration: {
        subject: "order",
        listSrc: "ecommerce/order",
        initialOrdering: { key: "createdAt", direction: "desc" },
        showIdField: true, // bool deafult true
        sortable: false, //bool, default false
        publishable: false, //bool, default false
        enableMassActions: false,
        editRoutePrefix: "admin-",
        createRoutePrefix: "admin-",
        deleteRoutePrefix: "ecommerce/",
        additionalListData: {
          simplified: false,
          translate: false,
        },
        actionListAsPopup: true,
        fields: {
          status: {
            itemKey: "status",
            label: "ecommerce.order.fields.status",
            filterable: {
              name: "status",
              type: "select2",
              placeholder: "ecommerce.order.fields.status",
              translateList: true,
              list: config.ecommerce.order.statuses.map((val) => ({
                value: val,
                label: `ecommerce.order.fields.statuses.${val}`,
              })),
            }, // boolean, or object
            quickEdit: {
              field: {
                type: "select",
                translateList: true,
                list: config.ecommerce.order.statuses.map((type) => ({
                  value: type,
                  label: "ecommerce.order.fields.statuses." + type,
                })),
              },
            },
            orderable: true,
          },
          createdAt: {
            itemKey: "createdAt",
            label: "ecommerce.order.fields.createdAt",
          },
          totalWithTax: {
            label: "ecommerce.order.fields.totalWithTax",
            orderable: false,
          },
          numProducts: {
            label: "ecommerce.order.fields.numProducts",
            orderable: false,
          },
          vendors: {
            label: "ecommerce.order.fields.vendors",
            orderable: false,
            filterable: {
              name: "vendorId",
              placeholder: "ecommerce.order.fields.vendor",
              type: "Select2",
              translateList: false,
              valueKey: "id",
              labelKey: "name",
              itemComponent: "iconItem",
              asyncListSource: "entity/Vendor",
              asyncListLive: true,
              textFilterAsSaffronListFilter: true,
              asyncDataTextFilterParamName: "title",
              multiple: false,
              asyncListData: {
                simplified: true,
              },
            },
          },
          BuyerId: {
            label: "ecommerce.order.fields.buyer",
            orderable: false,
            filterable: {
              name: "BuyerId",
              placeholder: "ecommerce.order.fields.buyer",
              type: "Select2",
              translateList: false,
              valueKey: "id",
              labelKey: "name",
              itemComponent: "iconItem",
              asyncListSource: "entity/User",
              asyncListLive: true,
              textFilterAsSaffronListFilter: true,
              asyncDataTextFilterParamName: "title",
              multiple: false,
              asyncListData: {
                simplified: true,
              },
            },
          },
          estimatedDeliveryDateTime: {
            itemKey: "estimatedDeliveryDateTime",
            label: "ecommerce.order.fields.estimatedDeliveryDateTime",
          },
          payments: {
            label: "ecommerce.order.fields.payment",
            orderable: false,
            filterable: false,
          },
          createdFrom: {
            listable: false,
            filterable: {
              name: "createdFrom",
              type: "DatePicker",
              placeholder: "ecommerce.order.fields.createdFrom",
            },
            type: "DatePicker",
            label: "ecommerce.order.fields.createdFrom",
          },
          createdBefore: {
            listable: false,
            filterable: {
              name: "createdBefore",
              type: "DatePicker",
              placeholder: "ecommerce.order.fields.createdBefore",
            },
            label: "ecommerce.order.fields.createdBefore",
          },
          estimatedDeliveryDateTimeBefore: {
            listable: false,
            filterable: {
              name: "estimatedDeliveryDateTimeBefore",
              type: "DatePicker",
              placeholder:
                "ecommerce.order.fields.estimatedDeliveryDateTimeBefore",
            },
            label: "ecommerce.order.fields.estimatedDeliveryDateTimeBefore",
          },
          estimatedDeliveryDateTimeAfter: {
            listable: false,
            filterable: {
              name: "estimatedDeliveryDateTimeAfter",
              type: "DatePicker",
              placeholder:
                "ecommerce.order.fields.estimatedDeliveryDateTimeAfter",
            },
            label: "ecommerce.order.fields.estimatedDeliveryDateTimeAfter",
          },
          productCodes: {
            label: "ecommerce.order.fields.productCodes",
            listable: false,
            filterable: {
              name: "productCodes",
              placeholder: "ecommerce.order.fields.productCodes",
            },
          },
        },
        actions: {
          create: false,
          show: false,
          edit: false,
          delete: false,
          custom: {
            quickShow: {
              //tooltip
              icon: "eye",
              theme: "lead",
              text: "ecommerce.order.admin.actions.view",
              handler: ({ item }) => {
                if (item) {
                  this.showOrder(item);
                }
              },
            },
            showPayment: {
              //tooltip
              icon: "credit-cards",
              theme: "lead",
              text: "ecommerce.order.admin.actions.seePayments",
              handler: ({ item }) => {
                if (item) {
                  this.showOrderPayments(item);
                }
              },
            },
            addReview: {
              //tooltip
              icon: "star",
              theme: "lead",
              text: "ecommerce.order.admin.actions.addReview",
              handler: ({ item }) => {
                if (item) {
                  this.goToCreateReview(item);
                }
              },
            },
          },
        },
      },
      isShowingOrderModal: false,
      orderPayments: [],
      isShowingOrderPayments: false,
      paymentForm: false,
      asyncData: {
        userForm: "entity/user/update",
        paymentForm: "entity/payment/update",
      },
    };
  },
  computed: {
    buyerInformationList() {
      const res = [];

      Object.entries(this.userForm.fields).forEach(([key, field]) => {
        let label;

        if (field.placeholder) {
          label = field.placeholder;
        }
        if (field.label) {
          label = field.label;
        }
        res.push({ label: label, value: this?.order?.Buyer[key] });
      });
      return res;
    },
  },
  mounted() {},
  methods: {
    showOrderPayments(order) {
      this.orderPayments = order.Payments;
      this.isShowingOrderPayments = true;
    },
    goToCreateReview() {
      this.$router.push({
        name: "admin-entity-entityType-create",
        params: { entityType: "review" },
      });
    },
    getPaymentSummaryForOrder(order) {
      let res = {
        total: 0,
        count: 0,
        status: "-",
      };
      if (!order.Payments || order.Payments.length < 1) {
        return res;
      }
      res.status = this.t("ecommerce.order.paymentStatus.complete");
      order.Payments.forEach((payment) => {
        res.total = res.total + payment.amount;

        if (payment.status !== "completed") {
          res.status = this.t("ecommerce.order.paymentStatus.incomplete");
        }
      });

      res.total = this.translateNumber(res.total, "currency", {
        currency: order.Quote.currency,
      });
      return res;
    },
    showOrder(order) {
      this.order = order;
      this.isShowingOrderModal = true;
    },
    playground(data) {

    },
    closeOrderModal() {
      this.isShowingOrderModal = false;
      if (this.orderWasUpdated) {
        this.$refs["crudList"].reloadItems(0);
      }

      this.orderWasUpdated = false;
    },
  },
};
</script>

<style scoped lang="scss"></style>
